import { ID } from '@griply/types';
import {
  useCreateCustomLifeAreasAllowed,
  useLifeAreasWithGoals,
} from 'features/life-areas';
import { useUpdateUserSettings, useUserHadTrial } from 'features/user';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumBanner } from 'shared/components/ui/premium-banner';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useUser } from 'shared/hooks/use-user';

import { useLifeAreaOverviewRows } from '../../use-life-area-overview-rows';
import { LifeAreaList } from '../life-area-list';
import { LifeAreaListCard } from '../life-area-list-card';
import { LifeAreaSortBar } from '../life-area-sort-bar';
import * as Styled from './overview.style';

export const Overview: React.FC = () => {
  const { t } = useTranslation();
  const { goals, lifeAreas } = useLifeAreasWithGoals();
  const user = useUser();
  const userHadTrial = useUserHadTrial();
  const scrollRef = useRef<HTMLDivElement>(null);

  const updateSettings = useUpdateUserSettings();
  const openPremiumDialog = useOpenPremiumDialog();
  const {
    allowed: createLifeAreaAllowed,
    enforced: createLifeAreaEnforced,
    limit: createLifeAreaLimit,
    total: createLifeAreaTotal,
  } = useCreateCustomLifeAreasAllowed();

  const {
    activeRows,
    frozenRows,
    archivedRows,
    sortMode,
    sortColumn,
    onSortChange,
  } = useLifeAreaOverviewRows(
    lifeAreas,
    goals,
    user?.settings?.lifeAreaSorting,
  );

  // Dragging sets an explicit manual order, so it clears any active column sort.
  const handleReorder = (ids: ID[]) => {
    onSortChange(undefined);
    updateSettings({ lifeAreaSorting: Array.from(new Set(ids)) });
  };

  const handleFrozenClick = () => openPremiumDialog();

  return (
    <Styled.Container ref={scrollRef}>
      <Styled.ListContainer>
        <LifeAreaSortBar
          column={sortColumn}
          sortMode={sortMode}
          onSortChange={onSortChange}
        />

        <LifeAreaList
          rows={activeRows}
          archivedRows={archivedRows}
          scrollElement={scrollRef}
          onReorder={handleReorder}
        />

        {!createLifeAreaAllowed && (
          <>
            <Styled.PremiumBannerContainer>
              <PremiumBanner
                title={t(
                  createLifeAreaEnforced
                    ? 'life-areas.premium-banner.title'
                    : 'pages.life-areas.premium-banner.not-enforced.title',
                  { total: createLifeAreaTotal, limit: createLifeAreaLimit },
                )}
                description={t(
                  createLifeAreaEnforced
                    ? userHadTrial
                      ? 'life-areas.premium-banner.description'
                      : 'pages.life-areas.premium-banner.try-for-free.description'
                    : 'pages.life-areas.premium-banner.not-enforced.description',
                  { limit: createLifeAreaLimit },
                )}
                onClick={openPremiumDialog}
                isTrial={!userHadTrial}
              />
            </Styled.PremiumBannerContainer>

            <Styled.StaticRows>
              {frozenRows.map((row) => (
                <LifeAreaListCard
                  key={row.id}
                  row={row}
                  onClick={handleFrozenClick}
                  isFrozen
                />
              ))}
            </Styled.StaticRows>
          </>
        )}
      </Styled.ListContainer>
    </Styled.Container>
  );
};
